import { combineReducers } from 'redux';
import { reduxRouterReducer } from 'store';
import { demoDataReducer } from 'modules/demoData/reducer';
import { allOrganizationsReducer } from 'modules/allOrganizations/reducer';
import analytics from 'modules/analytics/reducer';
import apiKeysReducer from 'modules/apiKeys/reducer';
import billingReducer from 'modules/billing/reducer';
import campaignAnalyticsReducer from 'modules/campaignAnalytics/reducer';
import campaignIndexReducer from 'modules/campaignIndex/reducer';
import { campaignFolderReducer } from 'modules/campaignIndex/campaignFolders/campaignFolderReducer';
import messagingInsightsReducer from 'modules/messagingInsights/reducer';
import userContextReducer from 'core/userContext/reducer';
import { adminAppReducer } from 'modules/adminEndpoints/reducer';
import metricDefinitionsReducer from 'modules/adminMetricDefinitions/reducer';
import { catalogPreviewReducer } from 'modules/catalog/catalogPreview/reducer';
import catalogCollectionBuilderReducer from 'modules/catalog/collectionBuilder/reducer';
import catalogCollectionsReducer from 'modules/catalog/collections/reducer';
import catalogFieldTypesReducer from 'modules/catalogFieldTypes/reducer';
import catalogTableReducer from 'modules/catalogTable/reducer';
import customEventsReducer from 'modules/customEvents/reducer';
import dashboardReducer from 'modules/dashboard/reducer';
import { dataSyncIndexReducer } from 'modules/dataSync/ducks/reducers';
import { dataFeedsReducer } from 'modules/dataFeeds/reducer';
import { dnsSetupReducer } from 'modules/dnsSetup/reducer';
import { enabledAccountsReducer } from 'modules/enabledAccounts/reducer';
import { eventsLogReducer } from 'modules/eventsLog/reducer';
import {
  chooseCampaignReducer,
  experimentAnalyticsPageReducer,
  experimentsIndexReducer,
} from 'modules/experiments/ducks/reducers';
import { experimentTakeoverReducer } from 'modules/experiments/takeover/ducks/reducers';
import featureFlagsReducer from 'modules/featureFlags/reducer';
import inboundSMSReducer from 'modules/inboundSMS/reducer';
import whatsAppMessagesReducer from 'modules/whatsappMessages/reducer';
import internalMetricsReducer from 'modules/internalMetrics/reducer';
import internalToolsReducer from 'modules/internalTools/reducer';
import { goalAnalyticsReducer } from 'modules/iterableGoals/ducks/reducers';
import { jobManagerReducer } from 'modules/jobManager/ducks/reducers';
import mailDomainsReducer from 'modules/mailDomains/reducer';
import metricsReducer from 'modules/metrics/reducer';
import mobileAppsReducer from 'modules/mobileApps/reducer';
import { navReducer } from 'modules/nav/ducks/reducer';
import newOrganizationReducer from 'modules/newOrganizationForm/reducer';
import notificationsReducer from 'modules/notificationCenter/reducer';
import {
  organizationMembersReducer,
  organizationPermissionsReducer,
  organizationProjectsReducer,
  organizationRolesReducer,
  organizationTeamsReducer,
} from 'modules/organization/reducer';
import projectsReducer from 'modules/projects/reducer';
import purchasesReducer from 'modules/purchases/reducer';
import queryBuilder from 'modules/queryBuilder/legacyQueryBuilder/reducer';
import reportChartReducer from 'modules/report/chartReducer';
import dataSourceModalReducer from 'modules/report/dataSourceModalReducer';
import deleteReportModalReducer from 'modules/report/deleteReportModalReducer';
import reportReducer from 'modules/report/reducer';
import reportTableReducer from 'modules/report/tableReducer';
import reportsTableReducer from 'modules/reportsTable/reducer';
import savedViewsIndexReducer from 'modules/savedViewsIndex/redux/reducer';
import { scheduledMessagesReducer } from 'modules/scheduledMessages/reducer';
import { sendersReducer } from 'modules/senders/reducer';
import { sentMessageReducer } from 'modules/sentMessage/reducer';
import systemWebhooksReducer from 'modules/systemWebhooks/reducer';
import templateIndexReducer from 'modules/templateIndex/ducks/reducer';
import templatePickerReducer from 'modules/templatePicker/ducks/reducer';
import templatePreviewReducer from 'modules/templatePreview/reducer';
import thirdPartyIntegrationsReducer from 'modules/thirdPartyIntegrations/reducer';
import toastsContainerReducer from 'modules/toastContainer/reducer';
import { userPreferencesReducer } from 'modules/userPreferences/reducer';
import webPushIntegrationsReducer from 'modules/webPush/reducer';
import { workflowIndexReducer } from 'modules/workflowIndex/reducer';
import workflowWebhooksReducer from 'modules/workflowWebhooks/reducer';
import workflowReducer from 'modules/workflows/ducks';

const reducers = combineReducers({
  admin: adminAppReducer,
  analytics,
  baus: combineReducers({
    featureFlags: featureFlagsReducer,
    organizationForm: newOrganizationReducer,
    internalMetrics: internalMetricsReducer,
    demoData: demoDataReducer,
    enabledAccounts: enabledAccountsReducer,
    projects: projectsReducer,
    allOrganizations: allOrganizationsReducer,
  }),
  campaignAnalytics: campaignAnalyticsReducer,
  campaignIndex: campaignIndexReducer,
  campaignFolder: campaignFolderReducer,
  catalogs: combineReducers({
    fieldTypes: catalogFieldTypesReducer,
    tables: catalogTableReducer,
    collections: catalogCollectionsReducer,
    collectionBuilder: catalogCollectionBuilderReducer,
    catalogPreviews: catalogPreviewReducer,
  }),
  dashboardState: dashboardReducer,
  dataSync: combineReducers({
    index: dataSyncIndexReducer,
  }),
  dataFeeds: dataFeedsReducer,
  experiments: combineReducers({
    index: experimentsIndexReducer,
    analyticsPage: experimentAnalyticsPageReducer,
    takeover: experimentTakeoverReducer,
    chooseCampaign: chooseCampaignReducer,
  }),
  insights: combineReducers({
    inboundSMS: inboundSMSReducer,
    events: eventsLogReducer,
    purchases: purchasesReducer,
    whatsAppMessages: whatsAppMessagesReducer,
  }),
  integrations: combineReducers({
    systemWebhooks: systemWebhooksReducer,
    thirdPartyIntegrations: thirdPartyIntegrationsReducer,
    webPushIntegrations: webPushIntegrationsReducer,
    workflowWebhooks: workflowWebhooksReducer,
  }),
  internalTools: internalToolsReducer,
  iterableGoals: combineReducers({
    goalAnalytics: goalAnalyticsReducer,
  }),
  jobManager: jobManagerReducer,
  messages: combineReducers({
    scheduled: scheduledMessagesReducer,
  }),
  metricDefinitions: metricDefinitionsReducer,
  metrics: metricsReducer,
  mobileApps: mobileAppsReducer,
  navigation: navReducer,
  notifications: notificationsReducer,
  organization: combineReducers({
    members: organizationMembersReducer,
    projects: organizationProjectsReducer,
    roles: organizationRolesReducer,
    teams: organizationTeamsReducer,
    permissions: organizationPermissionsReducer,
  }),
  queryBuilder,
  reports: reportReducer,
  reportDetails: combineReducers({
    dataSourceModal: dataSourceModalReducer,
    chartState: reportChartReducer,
    tableState: reportTableReducer,
    deleteReportModal: deleteReportModalReducer,
  }),
  reportsTable: reportsTableReducer,
  router: reduxRouterReducer,
  savedViewsIndex: savedViewsIndexReducer,
  sentMessage: sentMessageReducer,
  settings: combineReducers({
    apiKeys: apiKeysReducer,
    billing: billingReducer,
    customEvents: customEventsReducer,
    dnsSetup: dnsSetupReducer,
    mailDomains: mailDomainsReducer,
    preferences: userPreferencesReducer,
    senders: sendersReducer,
  }),
  messagingInsights: messagingInsightsReducer,
  templateIndex: templateIndexReducer,
  templatePicker: templatePickerReducer,
  templatePreview: templatePreviewReducer,
  toasts: toastsContainerReducer,
  userContext: userContextReducer,
  workflow: workflowReducer,
  workflowIndex: workflowIndexReducer,
});

export default reducers;
